import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Collapse, Button } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { post } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import CustomSelector from "../../../components/AuthenticationComponents/CustomSelector";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * AdminUsersCreatePage — create/edit form for users/members.
 * 
 * Supports both create and edit modes with appropriate validation.
 * Create mode requires: first_name, last_name, email, mobile, password, is_admin, is_free_member
 * Edit mode requires: first_name, last_name, email, mobile (password optional)
 * ---------------------------------------------------------------------- */

const AdminUsersCreatePage = ({ mode = "create", user }) => {
  document.title = mode === "create" ? "Create Member | Omaha Food Lovers" : "Edit Member | Omaha Food Lovers";
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [statusAccordionOpen, setStatusAccordionOpen] = useState(false);
  const [credentialsAccordionOpen, setCredentialsAccordionOpen] = useState(false);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Populate form from navigation state for edit mode
  useEffect(() => {
    if (mode === "edit" && userId) {
      const stateUserData = location.state?.userData;
      if (stateUserData) {
        // Handle both camelCase (from list page) and snake_case (from detail page/API)
        formik.setValues({
          first_name: stateUserData.firstName || stateUserData.first_name || "",
          last_name: stateUserData.lastName || stateUserData.last_name || "",
          email: stateUserData.email || "",
          mobile: stateUserData.mobile || "",
          password: "",
          password_confirmation: "",
          is_admin: (stateUserData.isAdmin !== undefined ? stateUserData.isAdmin : stateUserData.is_admin) ? 1 : 0,
          is_active: (stateUserData.isActive !== undefined ? stateUserData.isActive : stateUserData.is_active) ? 1 : 0,
          update_password: "",
          update_password_confirmation: "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, userId]);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must not exceed 50 characters"),
    last_name: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must not exceed 50 characters"),
    email: Yup.string().required("Email is required").email("Please enter a valid email address"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]{11}$/, "Mobile number must be 11 digits"),
    password:
      mode === "create"
        ? Yup.string()
            .required("Password is required")
            .min(6, "Password must be at least 6 characters")
        : Yup.string(),
    password_confirmation: Yup.string().test("password-match", "Passwords must match", function (value) {
      const { password } = this.parent;
      if (!password || password.length === 0) return true;
      return value === password;
    }),
    is_admin: Yup.number().required("User type is required").oneOf([0, 1], "Invalid user type"),
    is_active: Yup.number().required("Status is required").oneOf([0, 1], "Invalid status"),
    update_password: Yup.string().when("mode", {
      is: "edit",
      then: Yup.string().test("password-update", "Password must be at least 6 characters if provided", function (value) {
        if (!value || value.length === 0) return true;
        return value.length >= 6;
      }),
    }),
    update_password_confirmation: Yup.string().test("update-password-match", "Passwords must match", function (value) {
      const { update_password } = this.parent;
      if (!update_password || update_password.length === 0) return true;
      return value === update_password;
    }),
  });

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      password: "",
      password_confirmation: "",
      is_admin: 0,
      is_active: 1,
      update_password: "",
      update_password_confirmation: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setTouched, validateForm }) => {
      setTouched({
        first_name: true,
        last_name: true,
        email: true,
        mobile: true,
        password: true,
        password_confirmation: true,
        is_admin: true,
      });

      const errors = await validateForm();
      if (Object.keys(errors).length > 0) return;

      setLoading(true);

      try {
        const payload = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          mobile: values.mobile,
        };

        if (mode === "create") {
          payload.password = values.password;
          payload.password_confirmation = values.password_confirmation;
          payload.is_admin = values.is_admin;
          payload.is_free_member = 1;
        } else if (mode === "edit" && userId) {
          payload.id = userId;
          payload.is_admin = values.is_admin;
        }

        let response;
        if (mode === "create") {
          response = await post(endpoints.adminCreateUser, payload);
        } else if (mode === "edit" && userId) {
          response = await post(endpoints.adminUpdateUserData, payload);
        }

        if (response?.data?.success || response?.data?.status) {
          toastr.success(
            mode === "create" ? "User created successfully" : "User updated successfully"
          );
          navigate("/admin/users");
        }
      } catch (err) {
        if (err.response?.data?.errors) {
          const validationErrors = Object.values(err.response.data.errors)
            .filter((value) => Array.isArray(value))
            .flat();
          toastr.error(validationErrors.length > 0 ? validationErrors.join(", ") : extractErrorMessage(err));
        } else {
          toastr.error(extractErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const handleStatusUpdate = async () => {
    if (!formik.values.is_active && formik.values.is_active !== 0) return;
    
    setUpdatingStatus(true);
    try {
      const statusPayload = {
        id: userId,
        is_active: formik.values.is_active,
      };
      const response = await post(endpoints.adminChangeUserStatus, statusPayload);
      if (response?.data) {
        toastr.success("Status updated successfully");
        navigate("/admin/users");
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      toastr.error(extractErrorMessage(err));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePasswordUpdate = async () => {
    await formik.setTouched({
      update_password: true,
      update_password_confirmation: true,
    });
    
    const passwordErrors = await formik.validateForm();
    if (passwordErrors.update_password || passwordErrors.update_password_confirmation) {
      return;
    }

    if (!formik.values.update_password) {
      toastr.warning("Please enter a new password");
      return;
    }

    setUpdatingPassword(true);
    try {
      const passwordPayload = {
        id: userId,
        password: formik.values.update_password,
        confirmPassword: formik.values.update_password_confirmation,
      };
      const response = await post(endpoints.adminUpdateUserPasswordByAdmin, passwordPayload);
      if (response?.data) {
        toastr.success("Password updated successfully");
        formik.setValues({
          ...formik.values,
          update_password: "",
          update_password_confirmation: "",
        });
        navigate("/admin/users");
      }
    } catch (err) {
      console.error("Failed to update password:", err);
      toastr.error(extractErrorMessage(err));
    } finally {
      setUpdatingPassword(false);
    }
  };

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div className="rp-page-header">
            <h2 className="rp-page-header__title">
              {mode === "create" ? "Create Member" : "Edit Member"}
            </h2>
            <p className="rp-page-header__subtitle">
              {mode === "create" ? "Add a new member to the system" : "Update member information"}
            </p>
          </div>

          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="first_name"
                  label="First Name"
                  name="first_name"
                  type="text"
                  placeholder="Enter first name"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.first_name && !!formik.errors.first_name}
                  errorText={formik.errors.first_name}
                  className="white-bg"
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="last_name"
                  label="Last Name"
                  name="last_name"
                  type="text"
                  placeholder="Enter last name"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.last_name && !!formik.errors.last_name}
                  errorText={formik.errors.last_name}
                  className="white-bg"
                />
              </Col>
            </Row>

            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="email"
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.email && !!formik.errors.email}
                  errorText={formik.errors.email}
                  className="white-bg"
                  disabled={mode === "edit"}
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="mobile"
                  label="Mobile Number"
                  name="mobile"
                  type="text"
                  placeholder="Enter mobile number (11 digits)"
                  value={formik.values.mobile}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.mobile && !!formik.errors.mobile}
                  errorText={formik.errors.mobile}
                  className="white-bg"
                />
              </Col>
            </Row>

            {mode === "create" && (
              <>
                <Row>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="password"
                      label="Password"
                      name="password"
                      type="password"
                      placeholder="Enter password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.password && !!formik.errors.password}
                      errorText={formik.errors.password}
                      className="white-bg"
                    />
                  </Col>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="password_confirmation"
                      label="Confirm Password"
                      name="password_confirmation"
                      type="password"
                      placeholder="Confirm password"
                      value={formik.values.password_confirmation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.password_confirmation && !!formik.errors.password_confirmation
                      }
                      errorText={formik.errors.password_confirmation}
                      className="white-bg"
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomSelector
                      id="is_admin"
                      label="User Type"
                      name="is_admin"
                      placeholder="Select user type"
                      options={[
                        { value: 0, label: "Free User" },
                        { value: 1, label: "Admin" },
                      ]}
                      value={formik.values.is_admin}
                      onChange={(value) => formik.setFieldValue("is_admin", value)}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.is_admin && !!formik.errors.is_admin}
                      errorText={formik.errors.is_admin}
                      className="white-bg"
                    />
                  </Col>
                </Row>
              </>
            )}

            <div className="d-flex gap-2 mt-3">
              <SecondaryButton
                type="submit"
                variant="orange"
                disabled={loading}
                loading={loading}
                icon={tickIcon}
                iconPosition="right"
              >
                {loading ? "Saving..." : mode === "create" ? "Create Member" : "Update Profile"}
              </SecondaryButton>
              <SecondaryButton
                type="button"
                variant="light-grey"
                onClick={() => navigate("/admin/users")}
                icon={cancelIcon}
                iconPosition="right"
              >
                Cancel
              </SecondaryButton>
            </div>

            <hr className="mt-4 section-separator" />

            {mode === "edit" && (
              <>
                {/* Status Accordion */}
                <div className="accordion-section mt-4">
                  <Button
                    className="accordion-toggle w-100 text-left d-flex justify-content-between align-items-center"
                    onClick={() => setStatusAccordionOpen(!statusAccordionOpen)}
                    color="link"
                  >
                    <h5 className="mb-0">Account Status</h5>
                    <span>{statusAccordionOpen ? '−' : '+'}</span>
                  </Button>
                  <Collapse isOpen={statusAccordionOpen}>
                    <div className="accordion-content mt-3 mb-3" style={{ overflow: 'visible' }}>
                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomSelector
                            id="is_active"
                            label="Status"
                            name="is_active"
                            placeholder="Select status"
                            options={[
                              { value: 1, label: "Active" },
                              { value: 0, label: "Inactive" },
                            ]}
                            value={formik.values.is_active}
                            onChange={(value) => formik.setFieldValue("is_active", value)}
                            onBlur={formik.handleBlur}
                            invalid={formik.touched.is_active && !!formik.errors.is_active}
                            errorText={formik.errors.is_active}
                            className="white-bg"
                            menuPortalTarget={document.body}
                          />
                        </Col>
                      </Row>

                      <div className="d-flex gap-2 mt-3">
                        <SecondaryButton
                          type="button"
                          variant="orange"
                          disabled={updatingStatus}
                          loading={updatingStatus}
                          onClick={handleStatusUpdate}
                          icon={tickIcon}
                          iconPosition="right"
                        >
                          {updatingStatus ? "Updating..." : "Update Status"}
                        </SecondaryButton>
                        <SecondaryButton
                          type="button"
                          variant="light-grey"
                          onClick={() => navigate("/admin/users")}
                          icon={cancelIcon}
                          iconPosition="right"
                        >
                          Cancel
                        </SecondaryButton>
                      </div>
                    </div>
                  </Collapse>
                </div>

                {/* Credentials Accordion */}
                <div className="accordion-section mt-4">
                  <Button
                    className="accordion-toggle w-100 text-left d-flex justify-content-between align-items-center"
                    onClick={() => setCredentialsAccordionOpen(!credentialsAccordionOpen)}
                    color="link"
                  >
                    <h5 className="mb-0">Security Credentials</h5>
                    <span>{credentialsAccordionOpen ? '−' : '+'}</span>
                  </Button>
                  <Collapse isOpen={credentialsAccordionOpen}>
                    <div className="accordion-content mt-3">
                      <Row>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="update_password"
                            label="New Password (Optional)"
                            name="update_password"
                            type="password"
                            placeholder="Enter new password"
                            value={formik.values.update_password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={formik.touched.update_password && !!formik.errors.update_password}
                            errorText={formik.errors.update_password}
                            className="white-bg"
                          />
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={12}>
                          <CustomInput
                            id="update_password_confirmation"
                            label="Confirm New Password"
                            name="update_password_confirmation"
                            type="password"
                            placeholder="Confirm new password"
                            value={formik.values.update_password_confirmation}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={
                              formik.touched.update_password_confirmation && !!formik.errors.update_password_confirmation
                            }
                            errorText={formik.errors.update_password_confirmation}
                            className="white-bg"
                          />
                        </Col>
                      </Row>

                      <div className="d-flex gap-2 mt-3">
                        <SecondaryButton
                          type="button"
                          variant="orange"
                          disabled={updatingPassword}
                          loading={updatingPassword}
                          onClick={handlePasswordUpdate}
                          icon={tickIcon}
                          iconPosition="right"
                        >
                          {updatingPassword ? "Updating..." : "Update Password"}
                        </SecondaryButton>
                        <SecondaryButton
                          type="button"
                          variant="light-grey"
                          onClick={() => navigate("/admin/users")}
                          icon={cancelIcon}
                          iconPosition="right"
                        >
                          Cancel
                        </SecondaryButton>
                      </div>
                    </div>
                  </Collapse>
                </div>
              </>
            )}

          </Form>
        </div>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.Login.user,
});

export default connect(mapStateToProps, null)(AdminUsersCreatePage);
